import clsx from "clsx";
import { useEffect, useEffectEvent, useRef } from "react";
import { useIntl } from "react-intl";

import { LazyImage } from "@/components/Image/LazyImage";

type HotelGalleryThumbnailsProps = {
  images: { url: string; name: string }[];
  activeIndex: number;
  onSelect: (index: number) => void;
};

export function HotelGalleryThumbnails({
  images,
  activeIndex,
  onSelect,
}: HotelGalleryThumbnailsProps) {
  const intl = useIntl();
  const listRef = useRef<HTMLUListElement>(null);

  const scrollActiveIntoView = useEffectEvent(() => {
    if (listRef.current === null) return;
    const activeThumbnail = listRef.current.children[activeIndex] as
      | HTMLElement
      | undefined;
    activeThumbnail?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  });

  useEffect(() => {
    scrollActiveIntoView();
  }, [activeIndex]);

  useEffect(() => {
    if (listRef.current === null) return;
    const observer = new ResizeObserver(scrollActiveIntoView);
    observer.observe(listRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="shrink-0">
      <ul
        ref={listRef}
        // add padding to center first and last thumbnails — 50% of container width on each side,
        // offset by half the thumbnail size so the thumbnail centre aligns with the viewport centre.
        style={{ "--thumbnail-size": "calc(var(--spacing)*20)" }}
        className="-mx-[calc(var(--thumbnail-size)/2)] scrollbar-none flex gap-3 overflow-x-auto p-4 px-[50%]"
      >
        {images.map(({ url, name }, idx) => (
          <li key={url} className="shrink-0">
            <button
              type="button"
              onClick={() => onSelect(idx)}
              aria-label={intl.formatMessage(
                {
                  defaultMessage: "View image {number}",
                  description: "ARIA label for thumbnail button in gallery",
                },
                { number: idx + 1 },
              )}
              className={clsx(
                "block size-[var(--thumbnail-size)] overflow-hidden rounded-md border-2 transition-colors",
                idx === activeIndex
                  ? "border-white"
                  : "border-transparent opacity-50",
              )}
            >
              <LazyImage
                src={url}
                alt={intl.formatMessage(
                  {
                    defaultMessage: "{name} - Thumbnail {number}",
                    description: "Alt text for thumbnail image in gallery",
                  },
                  { name, number: idx + 1 },
                )}
                rootMargin="0px 100px 0px 100px"
                className="size-full object-cover"
              />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
